// Marks the current page `noindex` for as long as the calling component is
// mounted.
//
// Why this rather than a real 404 status: the app is a CRA SPA behind a
// catch-all rewrite, so every URL on the domain is served as index.html with
// HTTP 200 and React decides what to draw. React cannot set a status code, so
// a wrong URL is a "soft 404" — 200 OK carrying a page that says it does not
// exist. Left alone, that is an unbounded supply of indexable URLs all showing
// the same content.
//
// Google processes a noindex added during rendering, so this removes the
// indexing problem even though the status code stays 200. It is deliberately
// NOT paired with a canonical: Google's guidance is that noindex plus a
// canonical pointing elsewhere can carry the noindex over to the canonical
// target, and the target here would be the homepage.
//
// The page must stay crawlable for this to work at all — a path blocked in
// robots.txt is never fetched, so the noindex is never seen. Keep these paths
// out of the disallow list in scripts/build-guides.mjs.
import { useEffect } from 'react';

export function useNoIndex() {
  useEffect(() => {
    const existing = document.querySelector('meta[name="robots"]');

    // Reuse a tag if one is already there rather than appending a second one:
    // two robots tags is undefined behaviour, and StrictMode runs this effect
    // twice in development.
    if (existing) {
      const previous = existing.getAttribute('content');
      existing.setAttribute('content', 'noindex');
      return () => {
        if (previous === null) existing.removeAttribute('content');
        else existing.setAttribute('content', previous);
      };
    }

    const tag = document.createElement('meta');
    tag.setAttribute('name', 'robots');
    tag.setAttribute('content', 'noindex');
    document.head.appendChild(tag);
    return () => tag.remove();
  }, []);
}

export default useNoIndex;
